import type { ManualAlert } from '../../types';

export interface StudentAlertPresentation {
    summary: string;
    statusLabel: string | null;
    teacherNotes: string[];
    checkedAt: string | null;
    lateArrivalTime: string | null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function trimmedString(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
}

function studentNameFromAlert(alert: ManualAlert): string {
    const [firstStudent] = alert.students;
    if (firstStudent) {
        const studentName = [firstStudent.firstName, firstStudent.lastName]
            .map((part) => trimmedString(part))
            .filter((part): part is string => Boolean(part))
            .join(' ');

        if (studentName) return studentName;
        if (trimmedString(firstStudent.id)) return firstStudent.id.trim();
    }

    return 'Học sinh';
}

function classNameFromAlert(alert: ManualAlert, context: Record<string, unknown>): string | null {
    return trimmedString(context.className) ?? trimmedString(alert.class?.name);
}

function teacherNotesFromContext(context: Record<string, unknown>): string[] {
    if (!Array.isArray(context.teacherNotes)) return [];

    return context.teacherNotes
        .map((note) => trimmedString(note))
        .filter((note): note is string => Boolean(note));
}

export function getStudentAlertPresentation(alert: ManualAlert): StudentAlertPresentation | null {
    if (alert.source !== 'ATTENDANCE_STUDENT') return null;
    if (!isPlainObject(alert.context)) return null;

    const statusLabel = trimmedString(alert.context.statusLabel);
    const checkedAt = trimmedString(alert.context.checkedAt);

    const studentName = trimmedString(alert.context.studentName) ?? studentNameFromAlert(alert);
    const className = classNameFromAlert(alert, alert.context);
    const subject = statusLabel ? `${studentName} - ${statusLabel}` : studentName;

    return {
        summary: className
            ? `${subject} trong buổi học ${className}.`
            : `${subject}.`,
        statusLabel,
        teacherNotes: teacherNotesFromContext(alert.context),
        checkedAt,
        lateArrivalTime: trimmedString(alert.context.lateArrivalTime),
    };
}
